import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Card, CardContent, Typography } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import {
  APP_PATH_DASHBOARD,
  PLAN_TYPE,
  STRIPE_PAYMENT_STATUS_SUCCEEDED
} from '../../config';
import {
  getStripePriceIdSelector,
  getStripePriceSelector,
  stripePublishableKeySelector
} from '../../redux/features/configuration/configurationSlice';
import CardForm from '../CardForm';
import PricingTierCards from '../../components/PricingTierCards';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useGoToPath } from '../../hooks/useGoToPath';
import { trackEvent } from '../../helpers';
import { useDispatch, useSelector } from 'react-redux';
import {
  isLoadingSelector,
  subscriptionCleared,
  subscriptionSelector
} from '../../redux/features/invoice/invoicesSlice';
import { fetchUserJobs } from '../../redux/features/job/jobsSlice';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { usePlanStatus } from '../../hooks/usePlanStatus';
import { forceRefreshPlanStatus } from '../../redux/features/plan/plansSlice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  bundleSection: {
    marginBottom: '30px'
  },
  headerCard: {
    marginBottom: 20,
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  reassuranceBox: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, ${theme.palette.success.main}05 100%)`,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.primary.main}30`,
    textAlign: 'center'
  },
  reassuranceTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1)
  },
  reassuranceText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.6
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '2rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem'
    }
  }
}));

const PlanPackage = ({
  createSubscription,
  subscription: subscriptionProp,
  email,
  setMessageApp,
  isLoading: isLoadingProp,
  totalSelectedPlans,
  allSelectedPlanIDs,
  allSelectedPlans,
  onChangePlanSelectionCheckList,
  onChangePlanSelectionRadioList,
  onBrandColorChange,
  onTierSelected,
  jobId,
  jobRegionalRestrictions,
  brandColor,
  bundleOnly
}) => {
  const classes = useStyles();
  const { handleGoToPath } = useGoToPath(APP_PATH_DASHBOARD);
  const [selectedTier, setSelectedTier] = useState(null);
  const [tierBasedPrice, setTierBasedPrice] = useState(null);
  const [tierBasedPlanIds, setTierBasedPlanIds] = useState([]);
  const [stripePromise, setStripePromise] = useState(null);

  // Get Stripe publishable key from Redux
  const stripePublishableKey = useSelector(stripePublishableKeySelector);

  // Initialize Stripe promise when key is available
  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  // Fetch pricing from Redux store
  const starterPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_STARTER)
  );
  const starterPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.HIRING_PROFILE_STARTER)
  );

  const professionalPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL)
  );
  const professionalPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL)
  );

  const premiumPrice = useSelector(
    getStripePriceSelector(PLAN_TYPE.HIRING_PROFILE_PREMIUM)
  );
  const premiumPriceId = useSelector(
    getStripePriceIdSelector(PLAN_TYPE.HIRING_PROFILE_PREMIUM)
  );

  const [selectedProfilePlanId, setSelectedProfilePlanId] = useState(null); // 9, 10, or 11

  // Get loading state and subscription from Redux
  const isLoadingFromRedux = useSelector(isLoadingSelector);
  const subscriptionFromRedux = useSelector(subscriptionSelector);
  const { userId } = useCurrentUserInfo();

  // Get current plan status
  const { planStatus } = usePlanStatus();
  const currentPlanId = planStatus?.currentPlanId || 0;

  // Use Redux state, fallback to props if provided
  const isLoading =
    isLoadingProp !== undefined ? isLoadingProp : isLoadingFromRedux;
  const subscription = subscriptionProp || subscriptionFromRedux;
  const dispatch = useDispatch();

  // Clear any previous subscription state when component mounts
  useEffect(() => {
    console.log(
      '🔄 [PlanPackage] Component mounted, clearing previous subscription'
    );
    dispatch(subscriptionCleared());
  }, [dispatch]);

  // Restore selected plan from sessionStorage when component mounts
  useEffect(() => {
    const storedPlan = sessionStorage.getItem('selectedJobPlan');
    if (storedPlan) {
      try {
        const plan = JSON.parse(storedPlan);
        console.log(
          '🔄 [PlanPackage] Restoring plan from sessionStorage:',
          plan
        );

        // Map planId to tier id
        const planIdToTierId = {
          9: 'starter',
          10: 'professional',
          11: 'premium'
        };
        const tierId = planIdToTierId[plan.planId];

        if (tierId) {
          setSelectedTier(tierId);
          setSelectedProfilePlanId(plan.planId);

          // Get the tier data to set price and price IDs from Redux
          const tierData = {
            [PLAN_TYPE.HIRING_PROFILE_STARTER]: {
              price: starterPrice,
              priceId: starterPriceId,
              name: 'Starter Plan'
            },
            [PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL]: {
              price: professionalPrice,
              priceId: professionalPriceId,
              name: 'Professional Plan'
            },
            [PLAN_TYPE.HIRING_PROFILE_PREMIUM]: {
              price: premiumPrice,
              priceId: premiumPriceId,
              name: 'Premium Plan'
            }
          };

          const tier = tierData[plan.planId];
          if (tier) {
            setTierBasedPrice(tier.price);
            if (tier.priceId) {
              setTierBasedPlanIds([{ price: tier.priceId }]);
            }
          }
        }
      } catch (e) {
        console.error('❌ [PlanPackage] Failed to parse selected plan:', e);
      }
    }
  }, [
    premiumPrice,
    professionalPrice,
    starterPrice,
    starterPriceId,
    premiumPriceId,
    professionalPriceId
  ]);

  // Track when user views the plans page
  useEffect(() => {
    trackEvent('plans_page_viewed', {
      job_id: jobId || 'unknown',
      pre_selected_plans: allSelectedPlans.map((p) => p.title).join(', '),
      pre_selected_count: allSelectedPlans.length,
      pre_selected_total: totalSelectedPlans
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to dashboard after successful payment
  useEffect(() => {
    if (
      subscription &&
      subscription.status === STRIPE_PAYMENT_STATUS_SUCCEEDED
    ) {
      console.log(
        '✅ [PlanPackage] Payment succeeded, job published, navigating to Hiring Workspace'
      );
      handleGoToPath();
      // Clear subscription immediately after navigation to prevent stale state
      // This ensures the next job creation goes to plan package, not dashboard
      dispatch(subscriptionCleared());
      // Refresh plan status to update UI with purchased profile/plan
      console.log('🔄 [PlanPackage] Refreshing plan status after payment');
      dispatch(forceRefreshPlanStatus());
      // Clear selected plan from sessionStorage
      sessionStorage.removeItem('selectedJobPlan');
    }
  }, [subscription, handleGoToPath, dispatch]);

  const onPaymentSuccess = () => {
    // Navigate to dashboard (Hiring Workspace) - the activation will be handled by Redux thunk
    console.log(
      '✅ [PlanPackage] Job published successfully, redirecting to Hiring Workspace'
    );
    handleGoToPath();
    dispatch(fetchUserJobs({ userId }));
    // Clear selected plan from sessionStorage
    sessionStorage.removeItem('selectedJobPlan');
  };

  // Handle tier selection
  const handleTierSelect = (tier) => {
    console.log('🎯 [PlanPackage] Tier selected:', tier);
    setSelectedTier(tier.id);
    setTierBasedPrice(tier.price);

    // For bundled Hiring Profile plans (IDs 9, 10, 11), send single price ID
    // Format: [{ price: "price_id" }] to match backend expectations
    const bundledPlanId = tier.planId; // Single plan ID (9, 10, or 11)

    // Get the correct Stripe price ID based on bundled plan ID
    let stripePriceId = null;
    if (bundledPlanId === PLAN_TYPE.HIRING_PROFILE_STARTER) {
      stripePriceId = starterPriceId;
    } else if (bundledPlanId === PLAN_TYPE.HIRING_PROFILE_PROFESSIONAL) {
      stripePriceId = professionalPriceId;
    } else if (bundledPlanId === PLAN_TYPE.HIRING_PROFILE_PREMIUM) {
      stripePriceId = premiumPriceId;
    }

    const priceIds = stripePriceId ? [{ price: stripePriceId }] : [];

    setTierBasedPlanIds(priceIds);
    setSelectedProfilePlanId(bundledPlanId); // Store the profile plan ID (9, 10, or 11)

    console.log('💰 [PlanPackage] Tier price:', tier.price);
    console.log('🎫 [PlanPackage] Bundled plan ID:', bundledPlanId);
    console.log('🆔 [PlanPackage] Profile plan ID set:', bundledPlanId);
    console.log('📋 [PlanPackage] Stripe price ID:', stripePriceId);
    console.log('📋 [PlanPackage] Price IDs array:', priceIds);

    // Store selected plan in sessionStorage to preserve selection when navigating
    sessionStorage.setItem(
      'selectedJobPlan',
      JSON.stringify({
        planId: bundledPlanId,
        planName: tier.name,
        brandColor: tier.id === 'premium' ? brandColor : null
      })
    );

    // Notify parent component about tier selection for preview updates
    if (onTierSelected) {
      onTierSelected(tier);
    }

    // For bundled plans, we don't need to update individual plan selections
    // The backend will handle the bundle features
  };

  // The final price to charge from the selected tier
  const finalPrice = tierBasedPrice;

  // Use the selected tier's Stripe price ID
  // User must select a hiring profile bundle (Starter/Professional/Premium)
  const finalPlanIds = tierBasedPlanIds.length > 0
    ? tierBasedPlanIds // Selected tier card (products 9, 10, or 11)
    : allSelectedPlanIDs; // Fallback (shouldn't happen - tier selection is required)

  console.log('🔍 [PlanPackage] Pricing calculation:', {
    selectedTier,
    tierBasedPrice,
    tierBasedPlanIds,
    finalPrice,
    totalSelectedPlans,
    finalPlanIds
  });

  return (
    <>
      <Card className={classes.root + ' ' + classes.bundleSection}>
        <CardContent>
          <Typography className={classes.sectionTitle} variant="h2">
            Choose Your Job Pack
          </Typography>
          <PricingTierCards
            selectedTier={selectedTier}
            onTierSelect={handleTierSelect}
            brandColor={brandColor}
            onBrandColorChange={onBrandColorChange}
            currentPlanId={currentPlanId}
            bundleOnly={bundleOnly}
          />
        </CardContent>
      </Card>

      {/* Reassurance Box */}
      <Box className={classes.reassuranceBox}>
        <Typography className={classes.reassuranceTitle}>
          <LockIcon /> What happens when you click "Order and Pay"?
        </Typography>
        <Typography className={classes.reassuranceText}>
          Your payment will be processed, your pack will be immediately
          available, and you'll be redirected to your Hiring Workspace where you
          can start managing jobs and use the pack.
        </Typography>
      </Box>

      {!stripePromise ? (
        <Box textAlign="center" p={3}>
          <Typography>Loading payment configuration...</Typography>
        </Box>
      ) : (
        <Elements stripe={stripePromise}>
          <CardForm
          setMessageApp={(text, severity) => {
            setMessageApp(text, severity);
          }}
          subscription={subscription}
          totalPrice={finalPrice}
          emailInvoice={email}
          onClickPay={(paymentMethod) => {
            // Use selected tier if available, otherwise use current profile
            const profilePlanIdToSend =
              selectedProfilePlanId || currentPlanId || null;
            console.log('💳 [PlanPackage] Payment details:', {
              selectedTier: selectedProfilePlanId,
              currentProfile: currentPlanId,
              finalPlanId: profilePlanIdToSend
            });
            createSubscription(
              email,
              paymentMethod.id,
              finalPlanIds,
              jobId,
              profilePlanIdToSend
            );
          }}
          onPaymentSuccess={onPaymentSuccess}
          isLoading={isLoading}
        />
      </Elements>
      )}
    </>
  );
};

export default PlanPackage;
